import type { ReportTemplate } from '~/gql/reports/reports'
import { useMutation, useQuery } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { GQL_DELETE_REPORT_TEMPLATE, GQL_DUPLICATE_REPORT_TEMPLATE, GQL_REPORT_TEMPLATES } from '~/gql/reports/reports'
import { downloadFromApi } from '~/helpers/files/download-from-api'

/**
 * Les modèles de rapport de l'organisation (#163) et ce qu'on en fait depuis la
 * liste : télécharger (admin et formateur), dupliquer, supprimer (admin).
 */
export function useReportTemplates() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useQuery(GQL_REPORT_TEMPLATES)
  const templates: ReportTemplate[] = data?.reportTemplates ?? []
  const [duplicateTemplate] = useMutation(GQL_DUPLICATE_REPORT_TEMPLATE)
  const [deleteTemplate] = useMutation(GQL_DELETE_REPORT_TEMPLATE)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  /** `true` si l'action a abouti : l'écran n'en tire les conséquences qu'alors. */
  const run = async (action: () => Promise<unknown>, successKey: string): Promise<boolean> => {
    try {
      await action()
      await refetch()
      toast.success(t(successKey))
      return true
    } catch {
      toast.error(t('reports.error'))
      return false
    }
  }

  /** Un rapport trop gros est refusé par l'API : l'écran dit de filtrer, pas seulement « échec ». */
  const download = async (template: ReportTemplate) => {
    setDownloadingId(template.id)
    try {
      await downloadFromApi(`/reports/${template.id}/csv`, `${template.name}.csv`)
    } catch (e) {
      toast.error(t(e instanceof Error && e.message === 'REPORT_TOO_LARGE' ? 'reports.tooLarge' : 'reports.downloadFailed'))
    } finally {
      setDownloadingId(null)
    }
  }

  return {
    templates,
    loading,
    error,
    refetch,
    downloadingId,
    download,
    duplicate: (template: ReportTemplate, name: string) =>
      run(() => duplicateTemplate({ variables: { id: template.id, name } }), 'reports.duplicated'),
    remove: (template: ReportTemplate) =>
      run(() => deleteTemplate({ variables: { id: template.id } }), 'reports.deleted'),
  }
}
